import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { getCalApi } from '@calcom/embed-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { contact } from './content/agencyCopy';
import FrontechLogo from './components/FrontechLogo';
import AgencyHome from './components/marketing/AgencyHome';
import FrontProduct from './components/marketing/FrontProduct';
import FrontOSProduct from './components/marketing/FrontOSProduct';
import Assess from './components/marketing/Assess';
import FrontWidget from './components/marketing/FrontWidget';
import Login from './components/Login';
import Signup from './components/Signup';
import DashboardLayout from './components/DashboardLayout';
import AgentList from './components/AgentList';
import AgentBuilder from './components/AgentBuilder';
import AgentDetail from './components/AgentDetail';
import Analytics from './components/Analytics';
import Billing from './components/Billing';
import Privacy from './components/Privacy';
import Terms from './components/Terms';
import Refunds from './components/Refunds';

/*
 * Front runs on the marketing surface only.
 *
 * Not on /dashboard, /login or /signup: those are for people who already are
 * customers, where a sales agent in the corner is noise at best. It mounts
 * once and is never unmounted, because tearing the widget down and rebuilding
 * it on every route change would drop any conversation in progress.
 */
const WIDGET_EXCLUDED = ['/dashboard', '/login', '/signup'];

function FrontWidgetGate() {
  const { pathname } = useLocation();
  const excluded = WIDGET_EXCLUDED.some((p) => pathname.startsWith(p));
  return excluded ? null : <FrontWidget />;
}

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Defer so the destination route has time to render before we scroll.
      const target = hash;
      setTimeout(() => {
        const el = document.querySelector(target);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        else window.scrollTo(0, 0);
      }, 60);
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function CalInit() {
  useEffect(() => {
    (async () => {
      try {
        const cal = await getCalApi({ namespace: contact.calNamespace });
        cal('ui', {
          hideEventTypeDetails: false,
          layout: 'month_view',
          theme: 'dark',
          cssVarsPerTheme: {
            light: { 'cal-brand': '#00D67E' },
            dark:  { 'cal-brand': '#00D67E' },
          },
        });
      } catch {
        /* Cal.com script may fail to load offline / behind CSP — fine. */
      }
    })();
  }, []);
  return null;
}

function CursorGlowTracker() {
  useEffect(() => {
    let raf = 0;
    let latest = null;
    let active = null;
    const clear = (el) => {
      if (!el) return;
      el.style.removeProperty('--fa-mx');
      el.style.removeProperty('--fa-my');
    };
    const apply = () => {
      raf = 0;
      const el = latest && latest.target && latest.target.closest
        ? latest.target.closest('.fa-glow-text')
        : null;
      if (el !== active) {
        clear(active);
        active = el;
      }
      if (!el) return;
      const rect = el.getBoundingClientRect();
      el.style.setProperty('--fa-mx', latest.clientX - rect.left + 'px');
      el.style.setProperty('--fa-my', latest.clientY - rect.top + 'px');
    };
    const onMove = (e) => {
      latest = e;
      if (!raf) raf = requestAnimationFrame(apply);
    };
    const onLeaveWindow = () => {
      clear(active);
      active = null;
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerout', (e) => {
      if (!e.relatedTarget) onLeaveWindow();
    }, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  return null;
}

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--color-base)' }}>
      <div className="flex flex-col items-center gap-4">
        <FrontechLogo size={56} variant="loader" color="var(--color-accent)" />
        <span className="text-xs font-mono tracking-widest uppercase" style={{ color: 'var(--color-text-muted)' }}>
          FRONTECH
        </span>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <ScrollManager />
          <CursorGlowTracker />
          <CalInit />
          <FrontWidgetGate />
          <Routes>
            <Route path="/" element={<AgencyHome />} />
            <Route path="/front" element={<FrontProduct />} />
            <Route path="/front-os" element={<FrontOSProduct />} />
            <Route path="/assess" element={<Assess />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/refunds" element={<Refunds />} />
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<AgentList />} />
              <Route path="agents/new" element={<AgentBuilder />} />
              <Route path="agents/:id" element={<AgentDetail />} />
              <Route path="agents/:id/edit" element={<AgentBuilder />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="billing" element={<Billing />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}
